import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Edit, Trash2, Search, Users, Check, X, TrendingUp, UserPlus, AlertTriangle, Mail } from 'lucide-react';
import { crmAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';

// 2-Step Delete Confirmation Modal Component
function DeleteConfirmationModal({ customer, onConfirm, onCancel, isDeleting }) {
  const [step, setStep] = useState(1);
  const [confirmText, setConfirmText] = useState('');

  const customerName = customer?.firstName && customer?.lastName
    ? `${customer.firstName} ${customer.lastName}`
    : customer?.firstName || customer?.lastName || customer?.name || 'this customer';

  const handleFirstConfirm = () => {
    setStep(2);
  };

  const handleFinalConfirm = () => {
    if (confirmText.toLowerCase() === 'delete') {
      onConfirm();
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="rounded-xl shadow-xl max-w-md w-full" style={{ background: 'var(--bg-surface)' }}>
        <div className="p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
              <AlertTriangle className="w-6 h-6 text-red-600" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-[var(--text-primary)]">
                {step === 1 ? 'Delete Customer?' : 'Confirm Permanent Deletion'}
              </h2>
              <p className="text-sm text-[var(--text-secondary)]">
                {step === 1 ? 'This action cannot be undone' : 'Final confirmation required'}
              </p>
            </div>
          </div>

          {step === 1 ? (
            <>
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                <p className="text-sm text-red-800">
                  You are about to permanently delete <strong>{customerName}</strong> and all their associated data including:
                </p>
                <ul className="mt-2 text-sm text-red-700 list-disc list-inside">
                  <li>Customer profile information</li>
                  <li>Subscription and payment history</li>
                  <li>Any linked Stripe data</li>
                </ul>
              </div>
              <div className="flex gap-3">
                <button
                  onClick={onCancel}
                  className="flex-1 btn btn-outline"
                >
                  Cancel
                </button>
                <button
                  onClick={handleFirstConfirm}
                  className="flex-1 btn bg-red-600 hover:bg-red-700 text-white"
                >
                  Yes, Delete Customer
                </button>
              </div>
            </>
          ) : (
            <>
              <div className="mb-6">
                <p className="text-sm text-[var(--text-secondary)] mb-4">
                  To confirm deletion, please type <strong className="text-red-600">delete</strong> below:
                </p>
                <input
                  type="text"
                  value={confirmText}
                  onChange={(e) => setConfirmText(e.target.value)}
                  placeholder="Type 'delete' to confirm"
                  className="input w-full"
                  autoFocus
                />
              </div>
              <div className="flex gap-3">
                <button
                  onClick={() => { setStep(1); setConfirmText(''); }}
                  className="flex-1 btn btn-outline"
                >
                  Go Back
                </button>
                <button
                  onClick={handleFinalConfirm}
                  disabled={confirmText.toLowerCase() !== 'delete' || isDeleting}
                  className="flex-1 btn bg-red-600 hover:bg-red-700 text-white disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isDeleting ? 'Deleting...' : 'Permanently Delete'}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default function CRMPage() {
  const { studio } = useAuth();
  const queryClient = useQueryClient();
  const [searchQuery, setSearchQuery] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [customerToDelete, setCustomerToDelete] = useState(null);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    telephone: '',
    streetAddress: '',
    addressLine2: '',
    city: '',
    state: '',
    zipCode: '',
    country: 'USA',
    monthlyAmount: '',
    hasPaidSubscription: false,
    subscriptionStartDate: '',
    subscriptionEndDate: '',
    notes: ''
  });

  const studioId = studio?.id;

  // Fetch customers
  const { data: customersData, isLoading, error: customersError } = useQuery({
    queryKey: ['customers', studioId, searchQuery],
    queryFn: () => crmAPI.getCustomers(studioId, { search: searchQuery }),
    enabled: !!studioId,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
    refetchOnReconnect: true,
    staleTime: 30000 // 30 seconds - socket events will invalidate for real-time updates
  });


  // Create customer mutation
  const createMutation = useMutation({
    mutationFn: (data) => crmAPI.createCustomer(studioId, data),
    onSuccess: (response) => {
      queryClient.invalidateQueries(['customers', studioId]);
      queryClient.invalidateQueries(['monthly-revenue', studioId]);
      const emailSent = response?.data?.emailSent;
      if (emailSent) {
        toast.success('Customer created and invite email sent');
      } else {
        toast.success('Customer created (email not configured)');
      }
      setShowAddModal(false);
      resetForm();
    }
  });

  // Update customer mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => crmAPI.updateCustomer(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries(['customers', studioId]);
      queryClient.invalidateQueries(['monthly-revenue', studioId]);
      toast.success('Customer updated successfully');
      setShowAddModal(false);
      setEditingCustomer(null);
      resetForm();
    }
  });

  // Delete customer mutation
  const deleteMutation = useMutation({
    mutationFn: (id) => crmAPI.deleteCustomer(id),
    onSuccess: () => {
      queryClient.invalidateQueries(['customers', studioId]);
      queryClient.invalidateQueries(['monthly-revenue', studioId]);
      toast.success('Customer deleted successfully');
    }
  });

  // Resend invite mutation
  const resendInviteMutation = useMutation({
    mutationFn: (customerId) => crmAPI.resendInvite(studioId, customerId),
    onSuccess: () => {
      toast.success('Invite email sent successfully');
    },
    onError: (error) => {
      const errorMessage = error.response?.data?.message || 'Failed to send invite';
      toast.error(errorMessage);
    }
  });

  const resetForm = () => {
    setFormData({
      firstName: '',
      lastName: '',
      email: '',
      telephone: '',
      streetAddress: '',
      addressLine2: '',
      city: '',
      state: '',
      zipCode: '',
      country: 'USA',
      monthlyAmount: '',
      hasPaidSubscription: false,
      subscriptionStartDate: '',
      subscriptionEndDate: '',
      notes: ''
    });
  };

  // Simple, proven phone number formatter - formats as you type
  const formatPhoneNumber = (value) => {
    // Remove all non-digit characters
    const phoneNumber = value.replace(/\D/g, '');
    
    // Check if empty
    if (!phoneNumber) return '';
    
    // Check if number starts with 1 (country code) and has 11 digits
    const hasCountryCode = phoneNumber.length === 11 && phoneNumber[0] === '1';
    const digits = hasCountryCode ? phoneNumber.slice(1) : phoneNumber;
    
    // Format based on length
    if (digits.length < 4) {
      return hasCountryCode ? `+1 (${digits}` : `(${digits}`;
    } else if (digits.length < 7) {
      return hasCountryCode 
        ? `+1 (${digits.slice(0, 3)}) ${digits.slice(3)}`
        : `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    } else {
      return hasCountryCode
        ? `+1 (${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`
        : `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
    }
  };
  
  const handlePhoneChange = (e) => {
    const formatted = formatPhoneNumber(e.target.value);
    setFormData({ ...formData, telephone: formatted });
  };

  const handleEdit = (customer) => {
    setEditingCustomer(customer);
    // Handle backward compatibility: if name exists but firstName/lastName don't, split name
    let firstName = customer.firstName || '';
    let lastName = customer.lastName || '';
    if (!firstName && !lastName && customer.name) {
      const nameParts = customer.name.trim().split(/\s+/);
      firstName = nameParts[0] || '';
      lastName = nameParts.slice(1).join(' ') || '';
    }
    setFormData({
      firstName: firstName,
      lastName: lastName,
      email: customer.email || '',
      telephone: customer.telephone ? formatPhoneNumber(customer.telephone) : '',
      streetAddress: customer.streetAddress || '',
      addressLine2: customer.addressLine2 || '',
      city: customer.city || '',
      state: customer.state || '',
      zipCode: customer.zipCode || '',
      country: customer.country || 'USA',
      monthlyAmount: customer.monthlyAmount || '',
      hasPaidSubscription: customer.hasPaidSubscription || false,
      subscriptionStartDate: customer.subscriptionStartDate ? customer.subscriptionStartDate.split('T')[0] : '',
      subscriptionEndDate: customer.subscriptionEndDate ? customer.subscriptionEndDate.split('T')[0] : '',
      notes: customer.notes || ''
    });
    setShowAddModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const data = {
      ...formData,
      monthlyAmount: parseFloat(formData.monthlyAmount) || 0,
      subscriptionStartDate: formData.subscriptionStartDate || null,
      subscriptionEndDate: formData.subscriptionEndDate || null
    };
    
    console.log('Submitting customer data:', data);

    try {
      if (editingCustomer) {
        await updateMutation.mutateAsync({ id: editingCustomer.id, data });
      } else {
        await createMutation.mutateAsync(data);
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.response?.data?.error || 'Failed to save customer';
      console.error('Save customer error:', error.response?.data || error);
      toast.error(errorMessage);
    }
  };

  const handleDeleteClick = (customer) => {
    setCustomerToDelete(customer);
  };

  const handleDeleteConfirm = async () => {
    if (!customerToDelete) return;
    try {
      await deleteMutation.mutateAsync(customerToDelete.id);
      setCustomerToDelete(null);
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to delete customer';
      toast.error(errorMessage);
    }
  };

  const handleDeleteCancel = () => {
    setCustomerToDelete(null);
  };

  const customers = customersData?.data?.customers || [];

  // Handle API errors gracefully - only log once per error
  useEffect(() => {
    if (customersError && customersError.response?.status !== 404) {
      console.error('Error loading customers:', customersError);
    }
  }, [customersError]);

  // Calculate stats
  const totalCustomers = customers.length;
  const payingCustomers = customers.filter(c => c.hasPaidSubscription).length;

  // Calculate customers added this month
  const now = new Date();
  const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
  const customersThisMonth = customers.filter(c => {
    if (!c.createdAt) return false;
    const createdDate = new Date(c.createdAt);
    return createdDate >= startOfMonth;
  }).length;

  // Calculate new paying customers this month
  const newPayingCustomersThisMonth = customers.filter(c => {
    if (!c.hasPaidSubscription || !c.createdAt) return false;
    const createdDate = new Date(c.createdAt);
    return createdDate >= startOfMonth;
  }).length;

  // Calculate inactive customers
  const inactiveCustomers = customers.filter(c => !c.isActive).length;

  // Handle case when studio is not available
  if (!studio || !studioId) {
    return (
      <div className="space-y-6">
        <div className="card">
          <div className="text-center py-12">
            <p className="text-[var(--text-secondary)] mb-4">Studio information not available</p>
            <p className="text-sm text-[var(--text-muted)]">Please ensure you are logged in and your studio is set up.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 md:space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-[var(--text-primary)]">CRM</h1>
          <p className="text-sm md:text-base text-[var(--text-secondary)] mt-1 md:mt-2">Manage your customers and track revenue</p>
        </div>
        <button
          onClick={() => {
            resetForm();
            setEditingCustomer(null);
            setShowAddModal(true);
          }}
          className="btn btn-primary shadow-md hover:shadow-lg transition-all duration-300 text-sm md:text-base"
        >
          <Plus className="w-4 h-4 md:w-5 md:h-5 mr-1 md:mr-2" />
          Add Customer
        </button>
      </div>

      {/* Quick Stats - Compact */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="card py-3 group hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 border-2 border-transparent hover:border-[var(--border-subtle)]">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-[var(--text-secondary)] mb-1 group-hover:text-[var(--text-secondary)] transition-colors">Total Customers</p>
              <p className="text-2xl font-bold text-[var(--text-primary)] group-hover:text-[var(--text-primary)] transition-colors">{totalCustomers}</p>
            </div>
            <Users className="w-5 h-5 text-[var(--text-muted)] group-hover:text-[var(--text-secondary)] transition-colors" />
          </div>
        </div>
        <div className="card py-3 group hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 border-2 border-transparent hover:border-[var(--border-subtle)]">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-[var(--text-secondary)] mb-1 group-hover:text-[var(--text-secondary)] transition-colors">Paying Customers</p>
              <p className="text-2xl font-bold text-green-600 group-hover:text-green-700 transition-colors">{payingCustomers}</p>
            </div>
            <Check className="w-5 h-5 text-green-400" />
          </div>
        </div>
        <div className="card py-3 group hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 border-2 border-transparent hover:border-[var(--border-subtle)]">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-[var(--text-secondary)] mb-1 group-hover:text-[var(--text-secondary)] transition-colors">Added This Month</p>
              <p className="text-2xl font-bold text-purple-600 group-hover:text-purple-700 transition-colors">{customersThisMonth}</p>
            </div>
            <UserPlus className="w-5 h-5 text-purple-400" />
          </div>
        </div>
        <div className="card py-3 group hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 border-2 border-transparent hover:border-[var(--border-subtle)]">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-[var(--text-secondary)] mb-1 group-hover:text-[var(--text-secondary)] transition-colors">Monthly Growth Rate</p>
              <p className="text-2xl font-bold text-orange-600 group-hover:text-orange-700 transition-colors">
                {totalCustomers > 0 
                  ? `${((customersThisMonth / totalCustomers) * 100).toFixed(1)}%`
                  : '0%'}
              </p>
            </div>
            <TrendingUp className="w-5 h-5 text-orange-400" />
          </div>
        </div>
      </div>

      {/* Customers Table - Primary Focus */}
      <div className="card">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
          <h2 className="text-lg md:text-xl font-bold text-[var(--text-primary)]">Customers</h2>
          <div className="relative w-full sm:w-64">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[var(--text-muted)] w-4 h-4 md:w-5 md:h-5" />
            <input
              type="text"
              placeholder="Search customers..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="input pl-9 md:pl-10 text-sm md:text-base"
            />
          </div>
        </div>

        {isLoading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-b-4 border-primary-600 mx-auto"></div>
          </div>
        ) : customers.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-[var(--text-secondary)] mb-4">No customers found</p>
            <button
              onClick={() => {
                resetForm();
                setEditingCustomer(null);
                setShowAddModal(true);
              }}
              className="btn btn-primary shadow-md hover:shadow-lg transition-all duration-300"
            >
              <Plus className="w-5 h-5 mr-2" />
              Add Your First Customer
            </button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-[var(--border-subtle)]">
                  <th className="text-left py-3 px-4 text-sm font-semibold text-[var(--text-secondary)]">Name</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-[var(--text-secondary)]">Email</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-[var(--text-secondary)]">Telephone</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-[var(--text-secondary)]">Address</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-[var(--text-secondary)]">$/Month</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-[var(--text-secondary)]">Paid</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-[var(--text-secondary)]">Status</th>
                  <th className="text-right py-3 px-4 text-sm font-semibold text-[var(--text-secondary)]">Actions</th>
                </tr>
              </thead>
              <tbody>
                {customers.map((customer) => (
                  <tr key={customer.id} className="border-b border-[var(--border-subtle)] hover:bg-[var(--highlight-5)]">
                    <td className="py-3 px-4 text-[var(--text-primary)] font-medium">
                      {customer.firstName && customer.lastName 
                        ? `${customer.firstName} ${customer.lastName}`
                        : customer.firstName || customer.lastName || customer.name || 'N/A'}
                    </td>
                    <td className="py-3 px-4 text-[var(--text-secondary)]">{customer.email}</td>
                    <td className="py-3 px-4 text-[var(--text-secondary)]">{customer.telephone || '-'}</td>
                    <td className="py-3 px-4 text-[var(--text-secondary)]">
                      {customer.streetAddress || customer.addressLine2 || customer.city || customer.state || customer.zipCode
                        ? [
                            customer.streetAddress,
                            customer.addressLine2,
                            [customer.city, customer.state, customer.zipCode].filter(Boolean).join(' ')
                          ].filter(Boolean).join(', ') || '-'
                        : '-'}
                    </td>
                    <td className="py-3 px-4 text-[var(--text-primary)] font-medium">${parseFloat(customer.monthlyAmount || 0).toFixed(2)}</td>
                    <td className="py-3 px-4">
                      {customer.hasPaidSubscription ? (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          <Check className="w-3 h-3 mr-1" />
                          Yes
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                          <X className="w-3 h-3 mr-1" />
                          No
                        </span>
                      )}
                    </td>
                    <td className="py-3 px-4">
                      {customer.hasPaidSubscription ? (
                        customer.subscriptionEndDate ? (
                          <span className="text-sm text-[var(--text-secondary)]">
                            Until {new Date(customer.subscriptionEndDate).toLocaleDateString()}
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            Ongoing
                          </span>
                        )
                      ) : (
                        <span className="text-sm text-[var(--text-muted)]">-</span>
                      )}
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center justify-end gap-2">
                        {customer.hasAccount ? (
                          <span
                            className="p-2 text-green-600"
                            title="Account Active"
                          >
                            <Check className="w-4 h-4" />
                          </span>
                        ) : (
                          <button
                            onClick={() => resendInviteMutation.mutate(customer.id)}
                            disabled={resendInviteMutation.isLoading}
                            className="p-2 text-[var(--primary)] hover:text-[var(--primary-hover)] hover:bg-[var(--primary-10)] rounded-lg transition-colors disabled:opacity-50"
                            title="Resend Invite"
                          >
                            <Mail className="w-4 h-4" />
                          </button>
                        )}
                        <button
                          onClick={() => handleEdit(customer)}
                          className="p-2 text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:bg-[var(--highlight-5)] rounded-lg transition-colors"
                          title="Edit"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteClick(customer)}
                          className="p-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-lg transition-colors"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Add/Edit Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" style={{ background: 'var(--bg-surface)' }}>
            <div className="p-6 border-b border-secondary-200">
              <div className="flex items-center justify-between">
                <h2 className="text-2xl font-bold text-[var(--text-primary)]">
                  {editingCustomer ? 'Edit Customer' : 'Add New Customer'}
                </h2>
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => {
                      setShowAddModal(false);
                      setEditingCustomer(null);
                      resetForm();
                    }}
                    className="btn btn-outline shadow-sm hover:shadow-md transition-all duration-300 hover:bg-[var(--highlight-5)] hover:border-primary-300"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    form="customer-form"
                    className="btn btn-primary shadow-md hover:shadow-lg transition-all duration-300"
                    disabled={createMutation.isLoading || updateMutation.isLoading}
                  >
                    {editingCustomer ? 'Update' : 'Create'} Customer
                  </button>
                </div>
              </div>
            </div>
            <form id="customer-form" onSubmit={handleSubmit} className="p-6 space-y-6">
              {/* Customer Information Section */}
              <div className="space-y-4">
                <h3 className="text-sm font-semibold text-[var(--text-primary)] mb-3">Customer Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-[var(--text-secondary)] mb-1">
                      First Name *
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.firstName}
                      onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                      disabled={editingCustomer?.hasAccount}
                      className="input w-full disabled:opacity-60 disabled:cursor-not-allowed"
                      placeholder="John"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[var(--text-secondary)] mb-1">
                      Last Name *
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.lastName}
                      onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                      disabled={editingCustomer?.hasAccount}
                      className="input w-full disabled:opacity-60 disabled:cursor-not-allowed"
                      placeholder="Doe"
                    />
                  </div>
                </div>
                {editingCustomer?.hasAccount && (
                  <p className="text-xs text-[var(--text-muted)] -mt-2 mb-2">Profile fields are managed by the customer in their account settings</p>
                )}
                <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
                  <div className="md:col-span-8">
                    <label className="block text-sm font-medium text-[var(--text-secondary)] mb-1">
                      Email *
                    </label>
                    <input
                      type="email"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      disabled={editingCustomer?.hasAccount}
                      className="input w-full disabled:opacity-60 disabled:cursor-not-allowed"
                    />
                  </div>
                  <div className="md:col-span-4">
                    <label className="block text-sm font-medium text-[var(--text-secondary)] mb-1">
                      Telephone *
                    </label>
                    <input
                      type="tel"
                      required
                      value={formData.telephone}
                      onChange={handlePhoneChange}
                      disabled={editingCustomer?.hasAccount}
                      placeholder="(555) 123-4567"
                      maxLength={17}
                      className="input w-full disabled:opacity-60 disabled:cursor-not-allowed"
                    />
                  </div>
                </div>

                {/* Address Section */}
                <div className="space-y-3">
                  <h4 className="text-sm font-medium text-[var(--text-primary)]">Address</h4>
                  <div>
                    <label className="block text-sm font-medium text-[var(--text-secondary)] mb-1">
                      Street Address
                    </label>
                    <input
                      type="text"
                      value={formData.streetAddress}
                      onChange={(e) => setFormData({ ...formData, streetAddress: e.target.value })}
                      className="input w-full"
                      placeholder="123 Main St"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[var(--text-secondary)] mb-1">
                      Address Line 2 <span className="text-[var(--text-muted)] font-normal">(Optional)</span>
                    </label>
                    <input
                      type="text"
                      value={formData.addressLine2}
                      onChange={(e) => setFormData({ ...formData, addressLine2: e.target.value })}
                      className="input w-full"
                      placeholder="Apt 4B, Suite 200, etc."
                    />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
                    <div className="md:col-span-5">
                      <label className="block text-sm font-medium text-[var(--text-secondary)] mb-1">
                        City
                      </label>
                      <input
                        type="text"
                        value={formData.city}
                        onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                        className="input w-full"
                        placeholder="New York"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-[var(--text-secondary)] mb-1">
                        State
                      </label>
                      <input
                        type="text"
                        value={formData.state}
                        onChange={(e) => setFormData({ ...formData, state: e.target.value.toUpperCase() })}
                        className="input w-full"
                        placeholder="NY"
                        maxLength={2}
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-[var(--text-secondary)] mb-1">
                        Zip Code
                      </label>
                      <input
                        type="text"
                        value={formData.zipCode}
                        onChange={(e) => setFormData({ ...formData, zipCode: e.target.value })}
                        className="input w-full"
                        placeholder="10001"
                        maxLength={10}
                      />
                    </div>
                    <div className="md:col-span-3">
                      <label className="block text-sm font-medium text-[var(--text-secondary)] mb-1">
                        Country
                      </label>
                      <input
                        type="text"
                        value={formData.country}
                        onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                        className="input w-full"
                        placeholder="USA"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Subscription Information Section */}
              <div className="space-y-4 pt-4 border-t border-secondary-200">
                <h3 className="text-sm font-semibold text-[var(--text-primary)] mb-3">Subscription Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-[var(--text-secondary)] mb-1">
                      Paid Subscription
                    </label>
                    <div className="flex items-center h-10">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formData.hasPaidSubscription}
                          onChange={(e) => {
                            const isChecked = e.target.checked;
                            // Auto-populate monthlyAmount if checkbox is checked and amount is empty/zero
                            if (isChecked && (!formData.monthlyAmount || parseFloat(formData.monthlyAmount) === 0)) {
                              const defaultAmount = studio?.membershipPrice || 25.00;
                              setFormData({ 
                                ...formData, 
                                hasPaidSubscription: isChecked,
                                monthlyAmount: defaultAmount.toString()
                              });
                            } else {
                              setFormData({ ...formData, hasPaidSubscription: isChecked });
                            }
                          }}
                          className="w-4 h-4 text-[var(--text-secondary)] rounded"
                        />
                        <span className="text-sm text-[var(--text-secondary)]">Customer has active paid subscription</span>
                      </label>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[var(--text-secondary)] mb-1">
                      $/Month
                    </label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[var(--text-secondary)] font-medium">$</span>
                      <input
                        type="number"
                        step="0.01"
                        value={formData.monthlyAmount}
                        onChange={(e) => setFormData({ ...formData, monthlyAmount: e.target.value })}
                        className="input w-full pl-7"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[var(--text-secondary)] mb-1">
                      Subscription Start Date
                    </label>
                    <input
                      type="date"
                      value={formData.subscriptionStartDate}
                      onChange={(e) => setFormData({ ...formData, subscriptionStartDate: e.target.value })}
                      className="input w-full"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[var(--text-secondary)] mb-1">
                      Subscription End Date <span className="text-[var(--text-muted)] font-normal">(Optional)</span>
                    </label>
                    <div className="flex gap-2">
                      <input
                        type="date"
                        value={formData.subscriptionEndDate}
                        onChange={(e) => setFormData({ ...formData, subscriptionEndDate: e.target.value })}
                        className="input w-full"
                      />
                      {formData.subscriptionEndDate && (
                        <button
                          type="button"
                          onClick={() => setFormData({ ...formData, subscriptionEndDate: '' })}
                          className="btn btn-outline whitespace-nowrap"
                          title="Clear end date (make ongoing)"
                        >
                          Clear
                        </button>
                      )}
                    </div>
                    <p className="text-xs text-[var(--text-muted)] mt-1">
                      Leave blank for ongoing subscription until cancelled
                    </p>
                  </div>
                </div>
              </div>

              {/* Notes Section */}
              <div className="space-y-4 pt-4 border-t border-secondary-200">
                <div>
                  <label className="block text-sm font-medium text-[var(--text-secondary)] mb-1">
                    Notes
                  </label>
                  <textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    className="input w-full"
                    rows="3"
                  />
                </div>
              </div>
              <div className="flex justify-end gap-3 pt-4 border-t border-secondary-200">
                <button
                  type="button"
                  onClick={() => {
                    setShowAddModal(false);
                    setEditingCustomer(null);
                    resetForm();
                  }}
                  className="btn btn-outline"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary shadow-md hover:shadow-lg transition-all duration-300"
                  disabled={createMutation.isLoading || updateMutation.isLoading}
                >
                  {editingCustomer ? 'Update' : 'Create'} Customer
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {customerToDelete && (
        <DeleteConfirmationModal
          customer={customerToDelete}
          onConfirm={handleDeleteConfirm}
          onCancel={handleDeleteCancel}
          isDeleting={deleteMutation.isLoading}
        />
      )}
    </div>
  );
}
